import { sanitizeAddress } from '@/shared/models/addressModels'
import { PaymentDestination } from '@/shared/models/bankAcccountModel'
import { Company } from '@/shared/models/companyModel'
import {
    DeleteUser,
    PostUser,
    PostUserGateway,
    UpdateUser,
    UpdateUserGateway,
    UpdateUserPaymentDestination,
    User
} from '@/shared/models/userModel'
import { RequestResponse, requestService } from '@/shared/services/requestServices'
import { trackEvent } from '@/shared/utils/analyticsUtils'
import { batchFetch, resolveApiUrl } from '@/shared/utils/requestsUtils'
import { mapSort } from '@/shared/utils/sortUtils'
import { removeWhiteSpace } from '@/shared/utils/stringUtils'
import { QueryStringInput } from '@hello-aria/query-string'
import { isEmpty, isEqual, isMatch, isNil, omit, pickBy } from 'lodash'

interface GetUsers {
    companyId: Company['id']
    query?: QueryStringInput
    signal?: AbortSignal
}

export const getUsersService = batchFetch(async (options: GetUsers): Promise<RequestResponse<User[]> | undefined> => {
    const { companyId, query } = options || {}

    const url = resolveApiUrl('user.getAll', {
        query: {
            ...query,
            sorts: mapSort(query?.sorts, {
                usage: 'api'
            })
        },
        variables: { companyId }
    })

    const response = await requestService<User[]>(
        url,
        options.signal
            ? {
                  signal: options.signal
              }
            : undefined
    )

    if (response?.body) {
        response.body = response.body.map(mapUser)
    }

    return response
})

interface GetUser {
    companyId: string
    userId?: string
    hasBankAccount?: boolean
}

export const getUserService = async (params: GetUser): Promise<RequestResponse<User> | undefined> => {
    const { companyId, userId } = params
    if (!userId) return
    const url = resolveApiUrl('user.get', {
        variables: { companyId, userId },
        query: { params: { has_bank_account: String(params.hasBankAccount) } }
    })

    const response = await requestService<User>(url)

    if (response?.body) {
        response.body = mapUser(response.body)
    }

    return response
}

export const postUserService = async (
    payload: PostUser,
    companyId: Company['id']
): Promise<RequestResponse<User> | undefined> => {
    const formattedUser = mapPostUser(payload)
    const url = resolveApiUrl('user.create', { variables: { companyId } })
    const response = await requestService<User>(url, { method: 'POST', data: formattedUser })
    trackEvent('Create_User')
    return response
}

const sanitizePaymentDestinations = (
    paymentDestinations: PaymentDestination[] | undefined
): PaymentDestination[] | undefined => {
    if (isEmpty(paymentDestinations)) return undefined

    return paymentDestinations?.map((destination) => {
        const hasRoutingNumber = !isEmpty(destination.routingNumber?.trim())

        const sanitizedDestination = hasRoutingNumber
            ? destination
            : omit(destination, ['routingNumber', 'routingNumberType'])

        return {
            ...sanitizedDestination,
            accountNumber: removeWhiteSpace(sanitizedDestination.accountNumber)
        }
    })
}

export const mapPostUser = (payload: PostUser): PostUserGateway => {
    const sanitizedPaymentDestinations = sanitizePaymentDestinations(payload.paymentDestinations)

    return {
        ...omit(payload, 'paymentDestination'),
        email: removeWhiteSpace(payload.email),
        contractStatus: 'notSent' as const,
        address: sanitizeAddress(payload.address),
        businessIdentifier: {
            ...payload.businessIdentifier,
            country: payload.businessIdentifier.country,
            type: payload.businessIdentifier.type
        },
        paymentDestinations: sanitizedPaymentDestinations,
        canBeInvoiceOwner: payload.canBeInvoiceOwner
    }
}

export const mapUser = (user: User): User => {
    return {
        ...user,
        siren: user.siren ? removeWhiteSpace(user.siren) : undefined,
        bankAccount: {
            ...user.bankAccount,
            IBAN: removeWhiteSpace(user?.bankAccount?.IBAN ?? '')
        }
    }
}

export const updateUserService = async (
    payload: UpdateUser,
    companyId: Company['id']
): Promise<RequestResponse<User> | undefined> => {
    const formattedPayload: UpdateUserGateway = payload
    const url = resolveApiUrl('user.update', { variables: { companyId, userId: payload.id } })
    const response = await requestService<User>(url, { method: 'PATCH', data: formattedPayload })

    trackEvent('Update_User')

    return response
}

type State = 'create' | 'delete'

function removePaymentDestinationFromList(
    paymentDestinations: PaymentDestination[],
    paymentDestination: PaymentDestination
): PaymentDestination[] {
    const toRemoveCriteria = pickBy(
        {
            accountNumber: paymentDestination.accountNumber,
            routingNumber: paymentDestination.routingNumber
        },
        (value) => !isNil(value)
    )
    return paymentDestinations.filter((destination) => !isMatch(destination, toRemoveCriteria))
}

export function mapPaymentDestination(paymentDestination: Omit<PaymentDestination, 'type'>): PaymentDestination {
    const formattedPaymentDestination: PaymentDestination = {
        ...paymentDestination,
        type: 'BANK_ACCOUNT',
        ...(isEmpty(paymentDestination.routingNumber)
            ? { routingNumberType: undefined, routingNumber: undefined }
            : {
                  routingNumberType: paymentDestination.accountNumberType === 'IBAN' ? 'BIC' : 'SORT_CODE'
              })
    }

    return formattedPaymentDestination
}

export const updateUserPaymentDestinationsService = async (
    payload: UpdateUserPaymentDestination,
    companyId: Company['id'],
    state: State
): Promise<RequestResponse<User> | undefined> => {
    let formattedPayload: PaymentDestination[] = []
    const paymentDestinations = payload.user.paymentDestinations ?? []

    if (state === 'create') {
        const isDuplicate = paymentDestinations.some((destination) =>
            isEqual(destination.accountNumber, payload.paymentDestination.accountNumber)
        )
        if (isDuplicate) return Promise.reject(new Error('DUPLICATE_PAYMENT_DESTINATION'))

        const mappedPaymentDestination = mapPaymentDestination(payload.paymentDestination)
        formattedPayload = paymentDestinations.concat(mappedPaymentDestination)
    }

    if (state === 'delete')
        formattedPayload = removePaymentDestinationFromList(paymentDestinations, payload.paymentDestination)

    const url = resolveApiUrl('user.update', { variables: { companyId, userId: payload.user.id } })
    const response = await requestService<User>(url, {
        method: 'PATCH',
        data: {
            paymentDestinations: formattedPayload
        }
    })

    trackEvent('Update_User')

    return response
}

export const deleteUserService = async (
    companyId: Company['id'],
    payload: DeleteUser
): Promise<RequestResponse<User> | undefined> => {
    const url = resolveApiUrl('user.delete', {
        variables: { companyId, userId: payload.userId }
    })
    const response = await requestService<User>(url, { method: 'DELETE' })

    trackEvent('Delete_User')

    return response
}
